// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { Moves, Moves_Order_By } from '@gql/base-types';
import { useGetMoveSearchResultsCountQuery } from '../queries/getMoveSearchResultsCount.query.generated';
import { useGetMoveSearchResultsQuery } from '../queries/getMoveSearchResults.query.generated';

import { useSearchResults } from '@features/searchResults/providers/SearchResultsProvider';

import SearchResultsContent from '@features/searchResults/components/SearchResultsContent';

// TYPES ---------------------------------------------------------------- //

interface SearchResultsQueryProps {
  searchInput: string;
}

// COMPONENT ---------------------------------------------------------------- //

const SearchResultsQuery = ({ searchInput }: SearchResultsQueryProps) => {
  // Context variables
  const { end, limit, offset, order, orderBy, setOffset, start } = useSearchResults();

  // Data state variables
  const [moves, setMoves] = React.useState<Moves[]>([]);
  const [moveCount, setMoveCount] = React.useState<number>(0);

  // Reset offset to 0 when searchInput changes
  React.useEffect(() => {
    setOffset(0);
  }, [searchInput]);

  // Convert order/orderBy to GraphQL format
  const orderByGraphQL = React.useMemo(() => {
    const orderByArray = [];

    // Check for an orderBy
    if (orderBy) {
      // If the orderBy contains a nested field, build the nested orderBy structure
      if (orderBy.includes('.')) {
        const [parentField, childField] = orderBy.split('.');
        orderByArray.push({ [parentField]: { [childField]: `${order}_nulls_last` } });
      }
      // Otherwise, build the simple orderBy structure
      else {
        orderByArray.push({ [orderBy]: `${order}_nulls_last` });
      }
    }

    // Always add ready_by and id to the end of the array
    orderByArray.push({ ready_by: `desc` });
    orderByArray.push({ id: `desc` });

    // Return the array as Moves_Order_By[]
    return orderByArray as Moves_Order_By[];
  }, [orderBy, order]);

  // Fetch moves
  const { loading, error, data, refetch } = useGetMoveSearchResultsQuery({
    variables: {
      start,
      end,
      limit,
      offset,
      searchInputNumber: Number(searchInput) || 0,
      searchInputString: `%${searchInput}%`,
      orderBy: orderByGraphQL,
    },
    fetchPolicy: 'cache-first', // Use Apollo cache with real-time updates from subscription
    notifyOnNetworkStatusChange: true,
  });

  // Fetch counts
  const { data: countsData, refetch: countsRefetch } = useGetMoveSearchResultsCountQuery({
    variables: {
      start,
      end,
      searchInputNumber: Number(searchInput) || 0,
      searchInputString: `%${searchInput}%`,
    },
    fetchPolicy: 'cache-first',
  });

  // Update moves when data arrives
  React.useEffect(() => {
    if (data?.moves) {
      setMoves(data.moves as Moves[]);
    }
  }, [data]);

  // Update count when data arrives
  React.useEffect(() => {
    if (countsData) {
      setMoveCount(countsData?.moves_aggregate?.aggregate?.count || 0);
    }
  }, [countsData]);

  // Handle refetching the data
  const handleRefetch = async () => {
    await Promise.all([refetch(), countsRefetch()]);
  };

  return (
    <SearchResultsContent
      loading={loading}
      error={error}
      refetch={handleRefetch}
      moves={moves}
      moveCount={moveCount}
      searchInput={searchInput}
    />
  );
};

// EXPORT ---------------------------------------------------------------- //

export default SearchResultsQuery;
